import { Filter, Inbox, ShieldCheck, Star } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/design-system/lib/utils";
import { INBOX_SCOPE, type InboxScope } from "../inboxScopes";

export type { InboxScope };

interface Props {
  /** The NORMALISED scope — what the server will serve, not the raw URL value. */
  scope: InboxScope;
  canViewAll: boolean;
  onChange: (scope: InboxScope) => void;
}

interface RailEntry {
  scope: InboxScope;
  label: string;
  icon: LucideIcon;
  description: string;
  viewAllOnly?: boolean;
}

/**
 * The rail differs by audience, because the vocabulary must not lie: a caller
 * without Communications.ViewAll cannot see anybody else's entries, so for
 * them "All entries" would be a false promise and their own work IS the
 * default view. They get "My entries" as the first item; a see-all caller gets
 * "All entries" first and "My entries" as a real narrowing beneath it.
 *
 * Scope changes are propagated to the URL by the parent so the view is
 * bookmarkable. The default item stays OUT of the URL.
 */
const ENTRIES: RailEntry[] = [
  {
    scope: INBOX_SCOPE.All,
    label: "All entries",
    icon: Inbox,
    description: "Everything you can see.",
    viewAllOnly: true,
  },
  {
    scope: INBOX_SCOPE.Mine,
    label: "My entries",
    icon: Star,
    description: "Everything you logged.",
  },
  {
    scope: INBOX_SCOPE.Unplaced,
    label: "Unplaced",
    icon: Filter,
    description: "Single-placement entries — likely stragglers.",
  },
  {
    scope: INBOX_SCOPE.Compliance,
    label: "Compliance view",
    icon: ShieldCheck,
    description: "Everything, including deleted entries.",
    viewAllOnly: true,
  },
];

export function InboxLeftRail({ scope, canViewAll, onChange }: Props) {
  const visible = ENTRIES.filter((e) => !e.viewAllOnly || canViewAll);
  return (
    <aside aria-label="Inbox filters">
      <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
        Filters
      </div>
      <ul className="flex flex-col gap-[2px] m-0 p-0 list-none">
        {visible.map((entry) => {
          const Icon = entry.icon;
          const active = scope === entry.scope;
          return (
            <li key={entry.scope}>
              <button
                type="button"
                onClick={() => onChange(entry.scope)}
                aria-pressed={active}
                className={cn(
                  "w-full flex items-start gap-space-2 px-space-3 py-space-2 rounded-md text-left",
                  "transition-colors duration-fast ease-standard",
                  active
                    ? "bg-gold/15 text-onyx border-l-2 border-gold pl-[calc(theme(spacing.space-3)_-_2px)]"
                    : "text-onyx hover:bg-white-warm",
                )}
              >
                <Icon
                  size={16}
                  strokeWidth={1.5}
                  aria-hidden
                  className={cn(
                    "shrink-0 mt-[2px]",
                    active ? "text-gold-shadow" : "text-gray-mid",
                  )}
                />
                <span className="flex flex-col min-w-0">
                  <span className="font-display text-small font-semibold">
                    {entry.label}
                  </span>
                  <span className="text-label text-gray-mid">
                    {entry.description}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
